import CashCount, { TYPES as CASH_COUNT_TYPES } from 'src/models/cash/CashCount'
import CashTray from 'src/models/cash/CashTray'
import Model, { encoders, ID, Integer, IsoDateTime } from 'src/models/Model'
import RegisterStation from 'src/models/register/RegisterStation'
import User from 'src/models/user/User'

export default class CashSession extends Model {
  static name = 'CashSession'

  static versioned = true

  static employeeRequiredPermissionCategories = [Model.permissionCategories.PointOfSale, Model.permissionCategories.HardwareAndRegisterStation]

  static define () {
    this.fields = {
      id: {
        type: ID
      },

      cashCount: {
        type: CashCount,
        encode: encoders.hasOne,
        default: null,
        output: true
      },
      cashTray: {
        type: CashTray,
        encode: encoders.hasOne,
        default: null
      },
      registerStation: {
        type: RegisterStation,
        encode: encoders.hasOne,
        default: null
      },
      user: {
        type: User,
        encode: encoders.hasOne,
        default: null,
        output: true
      },

      sales: {
        type: Number,
        default: 0,
        output: true
      },
      saleCount: {
        type: Integer,
        output: true
      },
      refunds: {
        type: Number,
        default: 0,
        output: true
      },
      refundCount: {
        type: Integer,
        output: true
      },
      adjustments: {
        type: Number,
        default: 0,
        output: true
      },
      adjustmentCount: {
        type: Integer,
        output: true
      },
      creditCardSales: {
        type: Number,
        default: 0,
        output: true
      },
      creditCardSaleCount: {
        type: Integer,
        output: true
      },
      creditCardRefunds: {
        type: Number,
        default: 0,
        output: true
      },
      creditCardRefundCount: {
        type: Integer,
        output: true
      },

      notes: {
        type: String
      },

      openedAt: {
        type: IsoDateTime,
        output: true
      },
      closedAt: {
        type: IsoDateTime,
        output: true
      },

      initializeOption: {
        type: String,
        input: true,
        default: null
      },
      action: {
        type: String,
        input: true,
        default: null
      }
    }

    this.computed = {
      isOpen () {
        return !this.closedAt && this.isRemote
      },
      isClosed () {
        return !!this.closedAt
      },
      isUserRestricted () {
        // If cash session is restricted to a user
        if (this.cashCount?.type === CASH_COUNT_TYPES.CASH_TRAY && this.registerStation?.registerType?.enableRestrictedUserCashSessions) {
          // Only the owning user or an admin can open the cash session
          if (this.user?.id !== this.context.currentUser?.id && !this.context.currentUser?.admin) return true
        }

        return false
      },
      lastCashSession () {
        switch (this.cashCount?.type) {
          case CASH_COUNT_TYPES.CASH_TRAY:
            return this.registerStation?.lastCashSession
          case CASH_COUNT_TYPES.USER:
            return this.user?.lastCashSession
          default:
            return null
        }
      },
      canOpen () {
        if (this.isOpen) return false

        if (this.cashCount?.final) return false

        // Cannot reopen if another cash session has been opened since
        if (this.lastCashSession?.id !== this.id) return false

        if (this.isUserRestricted) return false

        return true
      },
      net () {
        return this.sales - this.refunds + this.adjustments
      },
      status () {
        return this.closedAt ? 'Closed' : 'Open'
      },
      // TODO INDY-3798 Decompose this into a custom component injected into model table
      statusChip () {
        if (this.status === 'Open') {
          return {
            label: 'Open',
            textColor: 'white',
            color: 'positive',
            size: 'sm'
          }
        } else if (this.status === 'Closed') {
          return {
            label: 'Closed',
            textColor: 'white',
            color: 'negative',
            size: 'sm'
          }
        }

        return {
          label: 'Unknown',
          textColor: 'black',
          color: 'info',
          size: 'sm'
        }
      }
    }

    this.queries = {
      cashSession: {
        arguments: {
          id: {
            type: ID,
            required: false
          },
          registerStationId: {
            type: ID,
            required: false
          },
          userId: {
            type: ID,
            required: false
          }
        },
        results: {
          cashCount: CashSession
        }
      }
    }
  }

  async open () {
    this.action = 'open'

    try {
      await this.save()
    } catch (error) {
      console.log(error)
    } finally {
      this.resetField('action')
    }
  }

  async close () {
    this.action = 'close'

    let result = null
    try {
      const saveResult = await this.save()

      result = { success: saveResult?.errors?.length === 0, errors: saveResult?.errors }
    } catch (error) {
      result = { success: false, errors: [error] }
    } finally {
      this.resetField('action')
    }

    return result
  }

  async closeAll () {
    this.action = 'close_all'

    let result = null
    try {
      const saveResult = await this.save()

      result = { success: saveResult?.errors?.length === 0, errors: saveResult?.errors }
    } catch (error) {
      result = { success: false, errors: [error] }
    } finally {
      this.resetField('action')
    }

    return result
  }
}
